import React, { useState, useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { useAuth } from '../context/AuthContext'
// import GameContainer from './game/GameContainer' // Archived
import { ToastMessage } from './ui/ToastNotification' // Only Type needed now
// import { analyzeNarrative, EmotionType, NarrativeResonance } from '../utils/semanticAnalyzer' // Unused here
import LobbyContainer from './lobby/LobbyContainer'
import { useGameSocket } from '../hooks/useGameSocket'


import GameArena from './game/GameArena'
import SetupMemories from './game/SetupMemories'
import GameOverScreen from './game/GameOverScreen'


import { audioManager } from '../utils/AudioManager'
import ChatWidget from './chat/ChatWidget'
import { sessionManager } from '../utils/sessionManager'

const GameApp: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [quickPlayState, setQuickPlayState] = useState<'IDLE' | 'JOINING' | 'WAITING_FOR_ROOM' | 'ADDING_BOT' | 'STARTING' | 'DONE'>('IDLE');

  const {
    socket,
    gameState,
    isConnected,
    isReconnecting,
    joinGame,
    startGame,
    addBot,
    updatePlayerTheme,
    error,
    clearError,

    leaveGame,
    chatMessages,
    sendChatMessage,
    unreadMessagesCount,
    markChatAsRead
  } = useGameSocket()

  // V3 Detection
  const gameStateV3 = (gameState as any)?.v3State;
  const phase = gameStateV3?.phase;

  // GESTION CENTRALISÉE DE LA MUSIQUE
  React.useEffect(() => {
    // Si on n'a pas de phase (Lobby par défaut) ou SETUP
    if (!phase || phase === 'LOBBY' || phase === 'SETUP') {
      audioManager.playMusic('landing');
    }
    // Si on est en jeu
    else if (phase === 'PLAYING' || phase === 'VERDICT') {
      audioManager.playMusic('game');
    }
    // Si la partie est finie
    else if (phase === 'FINISHED') {
      audioManager.playMusic('victory');
    }
  }, [phase]);

  // QUICK PLAY LOGIC
  useEffect(() => {
    if (location.state?.quickPlay && quickPlayState === 'IDLE' && isConnected) {
      setQuickPlayState('JOINING');
    }
  }, [location.state, quickPlayState, isConnected]);

  useEffect(() => {
    if (quickPlayState === 'JOINING') {
      const isCreating = !location.state?.roomCode;
      const roomCode = location.state?.roomCode || Math.floor(100000 + Math.random() * 900000).toString();
      const packId = location.state?.packId;
      const playerName = user?.username || `Joueur_${Math.floor(Math.random() * 1000)}`;

      setQuickPlayState('WAITING_FOR_ROOM');
      joinGame(playerName, 'Mes Aventures', packId, isCreating ? 2 : 4, roomCode, isCreating);
    }
  }, [quickPlayState, location.state, user, joinGame]);

  useEffect(() => {
    if (quickPlayState === 'WAITING_FOR_ROOM' && gameState?.id) {
      if (!location.state?.roomCode) {
        setQuickPlayState('ADDING_BOT');
        addBot('MEDIUM');
      } else {
        setQuickPlayState('DONE');
      }
    }
  }, [quickPlayState, gameState, addBot, location.state]);

  useEffect(() => {
    if (quickPlayState === 'ADDING_BOT') {
      const timer = setTimeout(() => {
        setQuickPlayState('STARTING');
        if (gameState?.id) {
          startGame(gameState.id, sessionManager.getPlayerId());
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [quickPlayState, gameState, startGame]);

  useEffect(() => {
    if (quickPlayState === 'STARTING' && phase === 'SETUP') {
      setQuickPlayState('DONE');
      // Clean up location state to prevent loop on refresh
      window.history.replaceState({}, '');
    }
  }, [quickPlayState, phase]);

  const handleSetupSubmit = (souvenirs: { text: string; answer: string; cells: number[] }[], shadowZones: number[]) => {
    if (socket && gameStateV3) {
      socket.emit('V3_ACTION_SETUP', {
        roomId: gameStateV3.roomId,
        souvenirs,
        shadowZones
      });
      // Optimistic UI or wait for update? Wait for update (isWaiting state handled inside component via server ack? or just local state)
    }
  };

  // Determine view
  let content;
  if (phase === 'SETUP') {
    const availableMemories = gameStateV3?.availableMemories || [];
    // Find local player to check readiness
    // We need sessionId to identify local player
    const localPlayerId = (socket as any)?.auth?.token || localStorage.getItem('reminiscence_session')?.match(/"playerId":"([^"]+)"/)?.[1];
    // Better way: import sessionManager
    const pId = localStorage.getItem('reminiscence_session') ? JSON.parse(localStorage.getItem('reminiscence_session')!).playerId : null;

    const localPlayer = gameStateV3?.players?.find((p: any) => p.id === pId);
    const isReady = localPlayer?.isReady || false;
    // [NEW] Get Theme for Template Replacement
    const redThread = localPlayer?.theme || "Mes Aventures";

    content = <SetupMemories onSubmit={handleSetupSubmit} isWaiting={isReady} packMemories={availableMemories} redThread={redThread} />;
  } else if (phase === 'PLAYING' || phase === 'VERDICT') {
    const verdictFinished = phase === 'VERDICT' && false; // Todo: Pass prop if needed
    content = <GameArena gameState={gameState} socket={socket} />;
  } else if (phase === 'FINISHED') {
    // Find winner
    const players = gameStateV3?.players || [];
    const winnerId = gameStateV3?.winnerId;
    const winner = players.find((p: any) => p.id === winnerId);

    content = (
      <GameOverScreen
        winner={winner}
        onBackToLobby={() => leaveGame()}
      />
    );
  } else {
    // Lobby
    content = (
      <LobbyContainer
        gameState={gameState}
        isConnected={isConnected}
        isReconnecting={isReconnecting}
        error={error}
        clearError={clearError}
        joinGame={joinGame}
        startGame={startGame}
        addBot={addBot}
        updatePlayerTheme={updatePlayerTheme}
        leaveGame={leaveGame}
      />
    );
  }

  if (quickPlayState !== 'IDLE' && quickPlayState !== 'DONE') {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-navy-950 text-gold-400 font-serif">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold-500 mb-6"></div>
        <h2 className="text-2xl font-bold animate-pulse text-center max-w-md">
          {quickPlayState === 'JOINING' || quickPlayState === 'WAITING_FOR_ROOM' ? "Recherche d'une connexion neuronale..." :
            quickPlayState === 'ADDING_BOT' ? "Appel à l'Archiviste (Adversaire IA)..." : "Ouverture du Cortex en cours..."}
        </h2>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden">
      {content}

      {/* Chat System (Phase 14) */}
      {/* Chat System */}
      {(phase === 'LOBBY' || phase === 'SETUP' || phase === 'PLAYING' || phase === 'VERDICT' || (!phase && gameState?.status === 'LOBBY') || (!phase && !gameState)) && (
        <ChatWidget
          messages={chatMessages}
          onSendMessage={sendChatMessage}
          currentUserId={sessionManager.getPlayerId()}
          unreadCount={unreadMessagesCount}
          onOpen={markChatAsRead}
        />
      )}
    </div>
  )
}


export default GameApp
